import { CalculatorFormData, CalculatorResults } from './types';
import { renderToString } from 'react-dom/server';
import React from 'react';
import { getRecommendedFeatures, calculateFeatureTotalImpact } from '../../data/roosterFeatures';

// We'll use this to format currencies
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
};

// Format numbers with thousands separators
const formatNumber = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
  }).format(value);
};

// Helper function to ensure numeric value
const getNumericValue = (value: number | string, defaultValue = 0): number => {
  if (typeof value === 'string') {
    return value === '' ? defaultValue : Number(value);
  }
  return value;
};

// Helper function to provide component descriptions from Screen 3 tooltips
const getComponentDescription = (componentName: string): string => {
  switch (componentName) {
    case 'Scheduling Velocity (SV)':
      return 'Measures how quickly interviews are scheduled and confirmed. This includes time from interview request to confirmation and reduction in scheduling-related delays.';
    case 'Scheduling Efficiency (SE)':
      return 'Measures the administrative effort and time required to coordinate interviews, including calendar management, communications, and logistics handling.';
    case 'Candidate Experience (CE)':
      return 'Evaluates how candidate-friendly your scheduling process is, including flexibility, convenience, and clarity of communications.';
    case 'Scheduling Integrity (SI)':
      return 'Assesses the consistency and dependability of your scheduling process, including no-show rates, cancellations, and rescheduling frequency.';
    case 'Scheduling Utilization (SU)':
      return 'Evaluates how effectively your process utilizes interviewer time, balances interviewer workloads, and maximizes resource availability.';
    default:
      return 'This component measures a critical aspect of your scheduling process efficiency.';
  }
};

// Helper function to provide improvement descriptions for each TAS component
const getComponentImprovementText = (componentName: string): string => {
  switch (componentName) {
    case 'Scheduling Velocity (SV)':
      return 'Self-scheduling capabilities and smart calendar integrations significantly reduce the time to confirm interviews.';
    case 'Scheduling Efficiency (SE)':
      return 'Automated scheduling workflows reduce administrative time spent coordinating interviews by 60-80%.';
    case 'Candidate Experience (CE)':
      return 'Candidate-controlled scheduling with personalized communications improves satisfaction and engagement.';
    case 'Scheduling Integrity (SI)':
      return 'Automated reminders and better scheduling options reduce rescheduling and no-show rates.';
    case 'Scheduling Utilization (SU)':
      return 'Smart interviewer selection and availability management ensure optimal resource allocation.';
    default:
      return 'Advanced scheduling technology provides comprehensive improvements across all aspects of the interview scheduling process.';
  }
};

// Component to render TAS Analysis Report for PDF capture
const TASAnalysisReport: React.FC<{
  formData: CalculatorFormData;
  results: CalculatorResults;
  componentData: any[];
  isRoosterBranded?: boolean;
  topPerformerTAS?: number;
  industryAverageTAS?: number;
}> = ({ formData, results, componentData, isRoosterBranded = true, topPerformerTAS = 75, industryAverageTAS = 50 }) => {
  return (
    <div className="pdf-content" style={{ fontFamily: 'Arial, sans-serif' }}>

      {/* Company Information */}
      <div className="organization-info" style={{
        border: '1px solid #E5E7EB',
        borderRadius: '4px',
        padding: '15px',
        marginBottom: '20px',
        backgroundColor: '#F9FAFB'
      }}>
        <h2 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#0284c7' }}>Organization Information</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>Industry:</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formData.industry}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>{formData.inputMode === 'fte' ? 'Total Employees:' : 'Annual Hires:'}</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatNumber(getNumericValue(formData.inputMode === 'fte' && formData.fteCount ? formData.fteCount : formData.annualHires))}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>Average Salary:</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatCurrency(getNumericValue(formData.avgSalary))}</div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>Daily Vacancy Cost:</div>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{formatCurrency(getNumericValue(formData.dailyVacancyCost))}</div>
          </div>
        </div>
      </div>
      
      {/* TAS Score Analysis */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 15px 0', fontSize: '20px', color: '#0284c7', borderBottom: '2px solid #0284c7', paddingBottom: '5px' }}>
          TAS Score Analysis
        </h2>

        <div className="tas-circle-container" style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '4px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          {/* Benchmarks Group */}
          <div style={{ display: 'flex', justifyContent: 'space-around', flex: '1' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '5px' }}>Industry Average</div>
              <div style={{
                width: '70px',
                height: '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#9CA3AF',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '20px',
                borderRadius: '50%',
                margin: '0 auto'
              }}>
                {industryAverageTAS}
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '5px' }}>Your Current Score</div>
              <div style={{
                width: '70px',
                height: '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#8B5CF6',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '20px',
                borderRadius: '50%',
                margin: '0 auto'
              }}>
                {results.currentTAS}
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '5px' }}>Top Performers</div>
              <div style={{
                width: '70px',
                height: '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#10B981',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '20px',
                borderRadius: '50%',
                margin: '0 auto'
              }}>
                {topPerformerTAS}
              </div>
            </div>
          </div>

          {/* Vertical Divider */}
          <div style={{
            width: '1px',
            height: '80px',
            backgroundColor: '#D1D5DB',
            margin: '0 30px'
          }}></div>

          {/* Projected Score */}
          <div style={{ flex: '0 0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '5px', fontWeight: '600' }}>
              Your Projected Score
              {isRoosterBranded && <div style={{ fontSize: '12px', color: '#059669', marginTop: '2px', fontWeight: 'normal' }}>with Rooster</div>}
            </div>
            <div style={{
              width: '80px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#0284c7',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '24px',
              borderRadius: '50%',
              margin: '0 auto',
              boxShadow: '0 4px 6px rgba(2, 132, 199, 0.3)'
            }}>
              {results.projectedTAS}
            </div>
            <div style={{ fontSize: '12px', color: '#059669', marginTop: '5px', fontWeight: '600' }}>
              +{results.projectedTAS - results.currentTAS} points
            </div>
          </div>
        </div>

        {/* Score explanation section */}
        <div style={{ marginBottom: '20px', backgroundColor: 'rgba(2, 132, 199, 0.1)', borderRadius: '4px', padding: '15px' }}>
          <p style={{ fontSize: '14px', margin: '0 0 10px 0' }}>
            The <strong>Talent Acquisition Scheduling</strong> score measures the efficiency and effectiveness of your interview scheduling process on a scale from 0-100.
          </p>

          <p style={{ fontSize: '14px', margin: '0 0 10px 0' }}>
            The average TAS score across {formData.industry} companies is around {industryAverageTAS}. Top performing organizations achieve scores of {topPerformerTAS} or higher.
            With your current score of {results.currentTAS}, your scheduling process is {results.currentTAS < industryAverageTAS ? 'below' : results.currentTAS > (industryAverageTAS + 10) ? 'above' : 'near'} the industry average.
          </p>

          <p style={{ fontSize: '14px', margin: '0 0 10px 0' }}>
            Based on your selected improvement level, {isRoosterBranded ? 'implementing Rooster Scheduling' : 'implementing advanced scheduling technology'} would increase your score to <strong>{results.projectedTAS}</strong> (a {formatNumber(getNumericValue(formData.tasImprovement))} point improvement), placing you in the <strong>{results.projectedTAS >= 75 ? 'top performing' : results.projectedTAS >= 70 ? 'high performing' : 'above average'}</strong> category.
          </p>

          {isRoosterBranded && (
            <p style={{ fontSize: '14px', margin: '0', fontStyle: 'italic' }}>
              Rooster clients typically see a 75% or greater improvement in their TAS score within the first 90 days of implementation.
            </p>
          )}
        </div>

        <p style={{ marginBottom: '15px', fontSize: '14px' }}>
          Your TAS score is composed of five key components, each weighted based on its impact on the overall scheduling process. Here's how you score in each area:
        </p>

        <div style={{ marginBottom: '20px' }}>
          {componentData.map((component, index) => {
            const barWidth = `${component.value}%`;

            return (
              <div key={index} style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{`${component.name} (${component.weight})`}</div>
                  <div style={{ fontWeight: 'bold', fontSize: '14px', color: component.color }}>{`${Math.round(component.value)}%`}</div>
                </div>
                <div style={{ width: '100%', backgroundColor: '#E5E7EB', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: barWidth,
                      height: '100%',
                      backgroundColor: component.color,
                      borderRadius: '5px 0 0 5px'
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#0284c7' }}>Component Descriptions</h3>

        <div style={{ marginBottom: '20px' }}>
          {componentData.map((component, index) => (
            <div key={index} style={{
              marginBottom: '15px',
              padding: '10px',
              border: `1px solid ${component.color}20`,
              borderLeft: `4px solid ${component.color}`,
              borderRadius: '4px',
              backgroundColor: '#FAFAFA'
            }} className="component-description">
              <div style={{ fontWeight: 'bold', fontSize: '14px', color: component.color, marginBottom: '5px' }}>{component.name}</div>
              <div style={{ fontSize: '14px' }}>{getComponentDescription(component.name)}</div>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '5px' }}>
                <strong>How {isRoosterBranded ? 'Rooster' : 'advanced scheduling technology'} improves this:</strong> {getComponentImprovementText(component.name)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Improvement Recommendations */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 15px 0', fontSize: '20px', color: '#0284c7', borderBottom: '2px solid #0284c7', paddingBottom: '5px' }}>
          Recommended Improvements
        </h2>
        
        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '15px' }}>
          Based on your TAS component scores
        </div>

        <p style={{ marginBottom: '20px', fontSize: '14px' }}>
          These features are specifically recommended for your organization based on your current TAS scores. 
          Each addresses your weakest components and contributes directly to improving your overall scheduling efficiency.
        </p>

        {/* Dynamic Recommendations Grid */}
        <div className="recommendations-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
          {(() => {
            // Get recommended features based on weak components
            const recommendedFeatures = getRecommendedFeatures(results.tasComponents, 8);
            
            return recommendedFeatures.slice(0, 8).map((feature, index) => (
              <div 
                key={index}
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  padding: '12px',
                  backgroundColor: '#FAFAFA',
                  borderLeft: '4px solid #0284c7',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                  <div style={{ fontSize: '20px' }}>{feature.icon || '🚀'}</div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '12px', color: '#0284c7', fontWeight: 'bold' }}>
                      Priority Feature
                    </div>
                  </div>
                </div>
                
                <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#1F2937', marginBottom: '5px' }}>
                  {feature.name}
                </div>
                
                <p style={{ fontSize: '12px', margin: '0 0 8px 0', color: '#4B5563', lineHeight: '1.4' }}>
                  {feature.whyItMatters}
                </p>
                
                <div style={{ 
                  borderTop: '1px solid #E5E7EB', 
                  paddingTop: '8px', 
                  marginTop: '8px',
                  textAlign: 'center'
                }}>
                  <span style={{ fontSize: '11px', color: '#6B7280' }}>
                    Impact: +{calculateFeatureTotalImpact(feature)} TAS points
                  </span>
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Footer is added directly in the HTML output for better print control */}
    </div>
  );
};

// Main function to generate and save the TAS PDF
export const generateTasPDFReport = async (
  formData: CalculatorFormData,
  results: CalculatorResults,
  getComponentData: () => any[],
  isRoosterBranded: boolean = true,
  enhancedData?: {
    topPerformerTAS?: number;
    industryAverageTAS?: number;
    componentData?: any[];
    currentTAS?: number;
    projectedTAS?: number;
  },
  sessionId: string = '',
  reportId?: string,
  isInline: boolean = false  // New parameter to display inline instead of popup
) => {
  
  // Create loading indicator
  const loadingDiv = document.createElement('div');
  loadingDiv.style.position = 'fixed';
  loadingDiv.style.top = '0';
  loadingDiv.style.left = '0';
  loadingDiv.style.width = '100%';
  loadingDiv.style.height = '100%';
  loadingDiv.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
  loadingDiv.style.display = 'flex';
  loadingDiv.style.justifyContent = 'center';
  loadingDiv.style.alignItems = 'center';
  loadingDiv.style.zIndex = '9999';
  loadingDiv.style.fontSize = '18px';
  loadingDiv.style.color = '#0284c7';
  loadingDiv.style.fontWeight = 'bold';
  loadingDiv.innerText = 'Generating report...';
  document.body.appendChild(loadingDiv);
  
  try {
    // Use fresh data from enhancedData if available, otherwise fall back to function calls
    const componentData = enhancedData?.componentData || getComponentData();

    // Use fresh TAS values if available
    const currentTAS = enhancedData?.currentTAS ?? results.currentTAS;
    const projectedTAS = enhancedData?.projectedTAS ?? results.projectedTAS;
    
    // Generate the HTML content
    const htmlContent = renderToString(
      <div className="pdf-container pdf-content" style={{ fontFamily: 'Arial, sans-serif', padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <TASAnalysisReport
          formData={formData}
          results={{...results, currentTAS, projectedTAS}}
          componentData={componentData}
          isRoosterBranded={isRoosterBranded}
          topPerformerTAS={enhancedData?.topPerformerTAS}
          industryAverageTAS={enhancedData?.industryAverageTAS}
        />
      </div>
    );
    
    // Determine target window based on isInline flag
    let targetDocument: Document;
    
    if (isInline) {
      // Display in current window (for ReportViewer)
      targetDocument = document;
      // Clear the current document content
      document.body.innerHTML = '';
      document.head.innerHTML = '';
    } else {
      // Open in new popup window (for normal report generation)
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        // Remove loading indicator before showing alert
        document.body.removeChild(loadingDiv);
        alert('Please allow popups for this website to generate the PDF report.\n\nTo enable popups:\n1. Look for the popup blocker icon in your browser\'s address bar\n2. Click it and select "Always allow popups from this site"\n3. Then try downloading the report again.');
        return;
      }
      
      targetDocument = printWindow.document;
      // Store reference to the window for PDF capture
      (window as any).reportWindow = printWindow;
    }

    // Get the current date for the filename
    const today = new Date();
    const dateString = today.toISOString().slice(0, 10);

    // Write the HTML directly to the target window with logo
    targetDocument.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>TAS Analysis Report</title>
        <meta charset="utf-8">
        <meta http-equiv="Content-Security-Policy" content="default-src 'self' 'unsafe-inline' 'unsafe-eval' data: blob: https:; connect-src 'self' https://us-central1-rooster-tas.cloudfunctions.net https://*.googleapis.com https://firestore.googleapis.com; img-src 'self' data: blob: https:;">
        <style>
          @media print {
            body { -webkit-print-color-adjust: exact; color-adjust: exact; }
            @page { size: A4; margin: 10mm; }

            /* Allow content to flow naturally */
            #content-with-footer {
              page-break-inside: auto;
              break-inside: auto;
              display: block !important;
              margin-top: 20px;
            }

            /* Keep the transform section and footer together */
            .transform-section {
              page-break-before: always !important;
              page-break-after: avoid !important;
              break-after: avoid !important;
            }

            /* Consistent padding for content */
            #content-with-footer {
              padding-top: 15px;
              margin-top: 10px;
            }

            /* Component descriptions can break if long */
            div[style*="borderLeft: 4px solid"] {
              page-break-inside: auto;
              break-inside: auto;
            }

            /* Ensure the footer stays with the transform section */
            .report-footer {
              page-break-before: avoid !important;
              break-before: avoid !important;
            }

            /* Page break rules - keep headings with their content */
            h2, h3 { 
              page-break-after: avoid;
              page-break-before: auto;
              orphans: 3;
              page-break-inside: avoid;
            }

            /* Force specific page breaks after certain sections */
            .recommendations-grid {
              page-break-before: always;
              page-break-inside: auto;
            }

            /* Better text flow settings */
            body {
              orphans: 3;
              widows: 3;
            }

            /* The logo and header should stay at the top of every page */
            .header-wrapper {
              display: table;
              width: 100%;
            }

            /* Only critical elements should avoid breaking */
            table { page-break-inside: avoid; }
            
            /* Ensure no breaks in the TAS score comparison section */
            .tas-circle-container { page-break-inside: avoid; }

            /* Allow component descriptions to flow naturally */
            .component-description {
              page-break-inside: auto;
              break-inside: auto;
            }

            /* Ensure header elements repeat properly on each page */
            .header-container, .logo-header {
              display: table-header-group;
            }

            /* Make sure measurements print with exact colors */
            * {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
            
            /* Hide the instruction panel when printing */
            .instruction-panel {
              display: none !important;
            }
          }
          
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
          }
          
          .content-wrapper {
            max-width: 800px;
            margin: 0 auto;
            background-color: white;
            box-shadow: 0 2px 10px rgba(0,0,0,0.05);
            padding: 0;
            overflow: hidden;
          }
          
          img { max-width: 100%; }
          svg { overflow: visible; max-width: 100%; }

          .logo-image {
            height: 60px;
            margin-bottom: 5px;
          }

          /* Header styles */
          .header-container {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #0284c7;
            color: white;
            padding: 15px;
            margin-bottom: 20px;
            width: auto;
            box-sizing: border-box;
          }

          .logo-container {
            display: flex;
            align-items: center;
          }
          
          /* Instructions panel */
          .instruction-panel {
            background-color: #F0F4FF;
            border: 1px solid #C7D2FE;
            border-radius: 8px;
            padding: 20px;
            margin: 0 auto 20px auto;
            max-width: 800px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          }
          
          .instruction-panel h2 {
            color: #4338CA;
            font-size: 18px;
            margin-top: 0;
            margin-bottom: 10px;
          }
          
          .instruction-panel p {
            color: #4B5563;
            font-size: 14px;
            line-height: 1.5;
            margin-bottom: 15px;
          }
          
          .button-container {
            display: flex;
            justify-content: center;
            gap: 15px;
            margin-top: 15px;
          }
          
          .action-button {
            background-color: #0284c7;
            color: white;
            border: none;
            border-radius: 4px;
            padding: 10px 20px;
            font-weight: bold;
            font-size: 14px;
            cursor: pointer;
            display: flex;
            align-items: center;
            box-shadow: 0 2px 4px rgba(2, 132, 199, 0.2);
          }
          
          .action-button:hover {
            background-color: #4338CA;
          }
          
          .action-button svg {
            margin-right: 8px;
            width: 16px;
            height: 16px;
          }
          
          .print-button {
            background-color: #ffffff;
            color: #0284c7;
            border: 1px solid #0284c7;
          }
          
          .print-button:hover {
            background-color: #F5F3FF;
          }

          @media print {
            .header-container {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              display: flex !important;
              background-color: #0284c7 !important;
              color: white !important;
            }
            /* Hide status overlay when printing */
            #status-overlay {
              display: none !important;
            }
          }
        </style>
      </head>
      <body>
        
        <!-- Instruction panel (will not be printed) - Green "Your Report is Ready" style -->
        <div class="instruction-panel" style="
          background: linear-gradient(to right, #f0fdf4, #ecfdf5);
          border: 1px solid #bbf7d0;
          color: #1f2937;
          padding: 24px;
          text-align: center;
          margin: 20px auto;
          border-radius: 8px;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
          max-width: 800px;
        ">
          <!-- Success checkmark -->
          <div style="
            display: flex;
            justify-content: center;
            margin-bottom: 12px;
          ">
            <div style="
              background-color: #dcfce7;
              border-radius: 50%;
              padding: 12px;
            ">
              <svg style="width: 32px; height: 32px; color: #16a34a;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          
          <h2 style="margin: 0 0 16px 0; font-size: 24px; font-weight: bold; color: #1f2937;">Your TAS Report is Ready</h2>
          
          ${reportId ? `
          <!-- Report Link Display - Enhanced -->
          <div style="margin-bottom: 24px; padding: 20px; background: linear-gradient(to bottom right, #eff6ff, #f0f9ff); border: 1px solid #bae6fd; border-radius: 8px; max-width: 36rem; margin-left: auto; margin-right: auto; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);">
            <div style="display: flex; align-items: start; gap: 8px; margin-bottom: 12px;">
              <!-- Bookmark Icon -->
              <svg style="width: 20px; height: 20px; color: #0284c7; margin-top: 2px; flex-shrink: 0;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"></path>
              </svg>
              <div style="flex: 1;">
                <p style="font-size: 16px; font-weight: 500; color: #374151; margin: 0;">Your permanent report link</p>
                <p style="font-size: 14px; color: #6b7280; margin: 4px 0 0 0;">Bookmark or save it - this link never expires</p>
              </div>
            </div>
            <div style="display: flex; align-items: center; justify-content: space-between; background: white; padding: 12px; border-radius: 4px; border: 1px solid #e5e7eb;">
              <code style="font-size: 14px; color: #374151; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; font-family: monospace;">
                ${window.location.origin}/tas-report/${reportId}
              </code>
              <button 
                type="button"
                id="copy-link-btn"
                style="margin-left: 8px; padding: 8px 16px; background: #0284c7; color: white; font-size: 14px; font-weight: 500; border-radius: 4px; border: none; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center;"
                onmouseover="if(!this.dataset.copied) this.style.background='#0369a1'; else this.style.background='#059669';"
                onmouseout="if(!this.dataset.copied) this.style.background='#0284c7'; else this.style.background='#10b981';"
                onclick="
                  const url = '${window.location.origin}/tas-report/${reportId}';
                  navigator.clipboard.writeText(url).then(() => {
                    const btn = document.getElementById('copy-link-btn');
                    if (btn) {
                      const originalHTML = btn.innerHTML;
                      btn.dataset.copied = 'true';
                      btn.innerHTML = '<svg style=\\'width: 16px; height: 16px; margin-right: 6px;\\' fill=\\'none\\' stroke=\\'currentColor\\' viewBox=\\'0 0 24 24\\'><path stroke-linecap=\\'round\\' stroke-linejoin=\\'round\\' stroke-width=\\'2\\' d=\\'M5 13l4 4L19 7\\'></path></svg>Copied!';
                      btn.style.background = '#10b981';
                      setTimeout(() => {
                        btn.innerHTML = originalHTML;
                        btn.style.background = '#0284c7';
                        delete btn.dataset.copied;
                      }, 2000);
                    }
                  }).catch(() => {
                    alert('Failed to copy. Please select and copy the URL manually.');
                  });
                "
              >
                <svg style="width: 16px; height: 16px; margin-right: 6px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                </svg>
                Copy Link
              </button>
            </div>
          </div>
          ` : ''}
          
          <!-- Action buttons - Two row layout for better hierarchy -->
          <div style="margin-bottom: 24px;">
            <!-- Primary Actions Row -->
            <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-bottom: 12px;">
              <button class="action-button" id="saveAsPdfButton" style="
                background: linear-gradient(to right, #0284c7, #06b6d4);
                color: white;
                padding: 12px 28px;
                border: none;
                border-radius: 6px;
                font-weight: 500;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                transition: all 0.2s;
                min-width: 180px;
              " onmouseover="this.style.background='linear-gradient(to right, #0369a1, #0891b2)'" onmouseout="this.style.background='linear-gradient(to right, #0284c7, #06b6d4)'">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 20px; height: 20px; margin-right: 8px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
                Download Report
              </button>
              
              ${reportId ? `
              <button class="action-button" id="emailReportButton" style="
                background: linear-gradient(to right, #dc2626, #ef4444);
                color: white;
                padding: 12px 28px;
                border: none;
                border-radius: 6px;
                font-weight: 500;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                transition: all 0.2s;
                min-width: 180px;
              " onmouseover="this.style.background='linear-gradient(to right, #b91c1c, #dc2626)'" onmouseout="this.style.background='linear-gradient(to right, #dc2626, #ef4444)'">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 20px; height: 20px; margin-right: 8px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email Report
              </button>
              ` : ''}
            </div>
            
            <!-- Secondary Actions Row -->
            <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap;">
              ${reportId ? `
              <button class="action-button" id="editReportButton" style="
                background: #f3f4f6;
                color: #4b5563;
                padding: 10px 24px;
                border: 1px solid #d1d5db;
                border-radius: 6px;
                font-weight: 500;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s;
                min-width: 160px;
              " onmouseover="this.style.background='#e5e7eb'; this.style.borderColor='#9ca3af';" onmouseout="this.style.background='#f3f4f6'; this.style.borderColor='#d1d5db';">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 18px; height: 18px; margin-right: 6px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Report
              </button>
              ` : ''}
              
              <button class="action-button" id="closeTabButton" style="
                background: #f3f4f6;
                color: #4b5563;
                padding: 10px 24px;
                border: 1px solid #d1d5db;
                border-radius: 6px;
                font-weight: 500;
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s;
                min-width: 160px;
              " onmouseover="this.style.background='#e5e7eb'; this.style.borderColor='#9ca3af';" onmouseout="this.style.background='#f3f4f6'; this.style.borderColor='#d1d5db';">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 18px; height: 18px; margin-right: 6px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                ${isInline ? 'Return' : 'Close'}
              </button>
            </div>
          </div>
        </div>

        <div class="content-wrapper">
          <!-- Header wrapper for proper paging -->
          <div class="header-wrapper">
            ${isRoosterBranded ? `
            <!-- Logo header row -->
            <div class="logo-header" style="display: flex; justify-content: center; padding: 15px; border-bottom: 1px solid #E5E7EB;">
              <img
                src="/images/rooster-logo.png"
                alt="Rooster"
                class="logo-image"
                style="max-height: 60px; width: auto;"
                onerror="this.style.display='none'"
              />
            </div>
            ` : ''}

            <!-- Blue header for the title -->
            <div class="header-container">
              <h1 style="margin: 0; font-size: 24px; width: 100%; text-align: center;">TAS Analysis Report</h1>
            </div>
          </div>

          <div style="padding: 0 20px 20px 20px;">
            <div class="report-content">
              <!-- Main content -->
              ${htmlContent}

              <!-- Transform section with footer as a single unit -->
              <div id="content-with-footer" style="break-inside: auto; page-break-before: auto; position: relative; margin-top: 0; padding-top: 5px;">
                <!-- Transform section manually created -->
                <div class="transform-section" style="
                  margin-top: 10px;
                  margin-bottom: 0;
                  padding: 25px 0;
                  background-color: rgba(2, 132, 199, 0.1);
                  border-radius: 8px;
                  text-align: center;
                ">
                  <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #0284c7;">Ready to Transform Your Interview Scheduling?</h3>

                  <div style="display: flex; justify-content: center; align-items: center; flex-wrap: wrap; max-width: 800px; margin: 0 auto;">
                    <div style="flex: 1 1 60%; padding: 0 15px; text-align: left; min-width: 300px;">
                      <p style="font-size: 15px; line-height: 1.4; margin-bottom: 10px; color: #374151;">
                        Based on your TAS analysis, ${isRoosterBranded ? 'Rooster Scheduling can help' : 'advanced scheduling technology can help'} your organization improve:
                      </p>
                      <ul style="font-size: 14px; line-height: 1.4; text-align: left; padding-left: 20px; margin: 0 0 10px 0; color: #374151;">
                        <li>Scheduling efficiency by up to 70%</li>
                        <li>Interview coordination and confirmation speed</li>
                        <li>Candidate experience and interview show rates</li>
                        <li>Recruiter and hiring manager productivity</li>
                      </ul>
                    </div>

                    ${isRoosterBranded ? `
                    <div style="flex: 1 1 40%; padding: 0 15px; display: flex; flex-direction: column; align-items: center; justify-content: center; min-width: 200px;">
                      <a href="https://rooster.us/demo" target="_blank" rel="noopener noreferrer" style="text-decoration: none;">
                        <div style="
                          padding: 10px 20px;
                          background-color: #0284c7;
                          color: white;
                          border-radius: 4px;
                          font-weight: bold;
                          font-size: 15px;
                          margin-bottom: 15px;
                          margin-top: 5px;
                          cursor: pointer;
                          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        ">
                          Schedule a Demo Today
                        </div>
                      </a>
                      <p style="font-size: 13px; margin: 0;">
                        www.rooster.us | sales@rooster.us
                      </p>
                    </div>
                    ` : ''}
                  </div>
                </div>

                <!-- Footer bar immediately after transform box -->
                <div class="report-footer" style="
                  background-color: #0284c7;
                  color: white;
                  padding: 8px 15px;
                  text-align: center;
                  font-size: 12px;
                  font-weight: normal;
                  margin-top: 20px;
                  border-radius: 0;
                ">
                  TAS Analysis Report | ${isRoosterBranded ? 'www.rooster.us | ' : ''}Generated on: ${new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Email Dialog Modal (hidden by default) -->
        <div id="emailDialog" style="
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 50;
          align-items: center;
          justify-content: center;
          padding: 16px;
        ">
          <div style="
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            max-width: 512px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
          ">
            <!-- Dialog Header -->
            <div style="
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 24px;
              border-bottom: 1px solid #e5e7eb;
            ">
              <div style="display: flex; align-items: center; gap: 8px;">
                <svg style="width: 24px; height: 24px; color: #dc2626;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h2 style="font-size: 20px; font-weight: 600; color: #111827; margin: 0;">Email Report</h2>
              </div>
              <button id="emailDialogClose" style="
                background: none;
                border: none;
                color: #9ca3af;
                cursor: pointer;
                padding: 0;
                transition: color 0.15s;
              " onmouseover="this.style.color='#6b7280'" onmouseout="this.style.color='#9ca3af'">
                <svg style="width: 24px; height: 24px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <!-- Dialog Body -->
            <form id="emailForm" style="padding: 24px;">
              <!-- Status Messages (initially hidden) -->
              <div id="emailSuccessMessage" style="
                display: none;
                align-items: center;
                gap: 8px;
                padding: 16px;
                background-color: #f0fdf4;
                border: 1px solid #86efac;
                border-radius: 8px;
                margin-bottom: 16px;
              ">
                <svg style="width: 20px; height: 20px; color: #16a34a; flex-shrink: 0;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p style="color: #166534; margin: 0;">Report sent successfully! Check your email.</p>
              </div>
              
              <div id="emailErrorMessage" style="
                display: none;
                align-items: center;
                gap: 8px;
                padding: 16px;
                background-color: #fef2f2;
                border: 1px solid #fecaca;
                border-radius: 8px;
                margin-bottom: 16px;
              ">
                <svg style="width: 20px; height: 20px; color: #dc2626; flex-shrink: 0;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p id="emailErrorText" style="color: #991b1b; margin: 0;">Failed to send email. Please try again.</p>
              </div>
              
              <!-- To Email -->
              <div style="margin-bottom: 16px;">
                <label for="recipientEmail" style="display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 4px;">
                  To <span style="color: #ef4444;">*</span>
                </label>
                <input type="email" id="recipientEmail" name="recipientEmail" autocomplete="email" required style="
                  width: 100%;
                  padding: 8px 12px;
                  border: 1px solid #d1d5db;
                  border-radius: 6px;
                  font-size: 14px;
                  box-sizing: border-box;
                  outline: none;
                  transition: all 0.15s;
                " placeholder="recipient@example.com" value="${formData.contactEmail || ''}"
                  onfocus="this.style.borderColor='#ef4444'; this.style.boxShadow='0 0 0 3px rgba(239, 68, 68, 0.1)'"
                  onblur="this.style.borderColor='#d1d5db'; this.style.boxShadow='none'">
              </div>
              
              <!-- Recipient Name -->
              <div style="margin-bottom: 16px;">
                <label for="recipientName" style="display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 4px;">
                  Recipient Name
                </label>
                <input type="text" id="recipientName" name="recipientName" autocomplete="name" style="
                  width: 100%;
                  padding: 8px 12px;
                  border: 1px solid #d1d5db;
                  border-radius: 6px;
                  font-size: 14px;
                  box-sizing: border-box;
                  outline: none;
                  transition: all 0.15s;
                " placeholder="John Doe" value="${formData.contactName || ''}"
                  onfocus="this.style.borderColor='#ef4444'; this.style.boxShadow='0 0 0 3px rgba(239, 68, 68, 0.1)'"
                  onblur="this.style.borderColor='#d1d5db'; this.style.boxShadow='none'">
              </div>
              
              <!-- CC Email -->
              <div style="margin-bottom: 16px;">
                <label for="ccEmail" style="display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 4px;">
                  CC (Optional)
                </label>
                <input type="email" id="ccEmail" name="ccEmail" autocomplete="off" style="
                  width: 100%;
                  padding: 8px 12px;
                  border: 1px solid #d1d5db;
                  border-radius: 6px;
                  font-size: 14px;
                  box-sizing: border-box;
                  outline: none;
                  transition: all 0.15s;
                " placeholder="cc@example.com"
                  onfocus="this.style.borderColor='#ef4444'; this.style.boxShadow='0 0 0 3px rgba(239, 68, 68, 0.1)'"
                  onblur="this.style.borderColor='#d1d5db'; this.style.boxShadow='none'">
              </div>
              
              <!-- Personal Message -->
              <div style="margin-bottom: 16px;">
                <label for="personalMessage" style="display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 4px;">
                  Personal Message (Optional)
                </label>
                <textarea id="personalMessage" name="personalMessage" autocomplete="off" rows="4" style="
                  width: 100%;
                  padding: 8px 12px;
                  border: 1px solid #d1d5db;
                  border-radius: 6px;
                  font-size: 14px;
                  box-sizing: border-box;
                  resize: vertical;
                  outline: none;
                  transition: all 0.15s;
                " placeholder="Add a personal note to accompany the report..."
                  onfocus="this.style.borderColor='#ef4444'; this.style.boxShadow='0 0 0 3px rgba(239, 68, 68, 0.1)'"
                  onblur="this.style.borderColor='#d1d5db'; this.style.boxShadow='none'"></textarea>
                <p style="font-size: 12px; color: #6b7280; margin: 4px 0 0;">
                  This message will appear in the email body along with the report summary.
                </p>
              </div>
              
              <!-- Action Buttons -->
              <div style="display: flex; justify-content: flex-end; gap: 12px; padding-top: 8px;">
                <button type="button" id="emailCancelButton" style="
                  padding: 8px 16px;
                  color: #374151;
                  border: 1px solid #d1d5db;
                  border-radius: 6px;
                  background: white;
                  font-size: 14px;
                  font-weight: 500;
                  cursor: pointer;
                  transition: all 0.15s;
                " onmouseover="this.style.backgroundColor='#f9fafb'" onmouseout="this.style.backgroundColor='white'">
                  Cancel
                </button>
                <button type="submit" id="emailSendButton" style="
                  padding: 8px 16px;
                  background: #dc2626;
                  color: white;
                  border: none;
                  border-radius: 6px;
                  font-size: 14px;
                  font-weight: 500;
                  cursor: pointer;
                  display: inline-flex;
                  align-items: center;
                  gap: 8px;
                  transition: all 0.15s;
                " onmouseover="this.style.background='#b91c1c'" onmouseout="this.style.background='#dc2626'">
                  <svg style="width: 16px; height: 16px;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Report
                </button>
              </div>
            </form>
          </div>
        </div>
        
        <script>
          // Function to save as PDF (uses print dialog but with suggested settings)
          function saveAsPDF() {
            // Set title to include useful file name
            document.title = "TAS-Analysis-${dateString}";
            
            // Industry-standard cross-browser print solution
            setTimeout(function() {
              try {
                // Try execCommand first (works better in Safari despite being deprecated)
                document.execCommand('print', false, null);
              } catch(e) {
                // Fallback to window.print() for browsers that don't support execCommand
                window.print();
              }
            }, 250); // Small delay ensures content is fully loaded
          }
          
          // Add event listener to save button
          document.getElementById('saveAsPdfButton').addEventListener('click', saveAsPDF);
          
          ${reportId ? `
          
          // Add event listener to email button
          const emailButton = document.getElementById('emailReportButton');
          if (emailButton) {
            emailButton.addEventListener('click', function() {
              // Show the email dialog
              const dialog = document.getElementById('emailDialog');
              if (dialog) {
                dialog.style.display = 'flex';
              }
            });
          }
          
          // Add event listener to edit button
          const editButton = document.getElementById('editReportButton');
          if (editButton) {
            editButton.addEventListener('click', function() {
              // Navigate to calculator with edit parameter - use TAS-specific route
              const editUrl = '${formData.calculatorPath || '/'}?editReport=${reportId}';
              ${isInline ? `
              // For inline display, navigate in current window
              window.location.href = editUrl;
              ` : `
              // For popup, open in parent window and close popup
              if (window.opener && !window.opener.closed) {
                window.opener.location.href = editUrl;
                window.close();
              } else {
                // If no parent window, navigate in current window
                window.location.href = editUrl;
              }
              `}
            });
          }
          ` : `
          // No reportId provided, buttons will not be functional
          `}
          
          // Add event listener to close button
          document.getElementById('closeTabButton').addEventListener('click', function() {
            ${isInline ? `
            // For inline display, check if accessed from admin panel
            const urlParams = new URLSearchParams(window.location.search);
            const fromAdmin = urlParams.get('from') === 'admin';
            if (fromAdmin) {
              // Use popup-style return for admin panel - focus parent tab and close
              try {
                if (window.opener && !window.opener.closed) {
                  window.opener.focus();
                }
                window.close();
              } catch (error) {
                // Fallback if tab closing fails
                window.location.href = '/management';
              }
            } else if (window.history.length > 1) {
              window.history.back();
            } else {
              // Fallback: detect current route and navigate appropriately
              const currentPath = window.location.pathname;
              if (currentPath.includes('/tas-report/')) {
                // Try to determine original route from referrer or default to /tas
                window.location.href = document.referrer && document.referrer.includes('/rooster-tas') ? '/rooster-tas' : '/tas';
              } else {
                window.location.href = '/';
              }
            }
            ` : `
            // For popup window, focus parent and close
            try {
              // Focus the parent window if it exists and is not closed
              if (window.opener && !window.opener.closed) {
                window.opener.focus();
              }
              
              // Close the popup window
              window.close();
              
              // Fallback check if window didn't close (some browsers restrict this)
              setTimeout(function() {
                if (!window.closed) {
                  alert('Please close this tab manually to return to the calculator.');
                }
              }, 100);
            } catch (error) {
              // Fallback for any errors
              window.close();
              if (!window.closed) {
                alert('Please close this tab manually to return to the calculator.');
              }
            }
            `}
          });
          
          ${reportId ? `
          // Email dialog functionality
          const emailDialogClose = document.getElementById('emailDialogClose');
          const emailCancelButton = document.getElementById('emailCancelButton');
          const emailDialog = document.getElementById('emailDialog');
          
          function closeEmailDialog() {
            if (emailDialog) {
              emailDialog.style.display = 'none';
              // Reset messages
              document.getElementById('emailSuccessMessage').style.display = 'none';
              document.getElementById('emailErrorMessage').style.display = 'none';
            }
          }
          
          if (emailDialogClose) {
            emailDialogClose.addEventListener('click', closeEmailDialog);
          }
          
          if (emailCancelButton) {
            emailCancelButton.addEventListener('click', closeEmailDialog);
          }
          
          // Handle form submission
          const emailForm = document.getElementById('emailForm');
          if (emailForm) {
            emailForm.addEventListener('submit', async function(e) {
              e.preventDefault();
              
              // Get form values
              const recipientEmail = document.getElementById('recipientEmail').value;
              const recipientName = document.getElementById('recipientName').value;
              const ccEmail = document.getElementById('ccEmail').value;
              const personalMessage = document.getElementById('personalMessage').value;
              
              // Hide previous messages
              document.getElementById('emailSuccessMessage').style.display = 'none';
              document.getElementById('emailErrorMessage').style.display = 'none';
              
              // Disable send button and show loading state
              const sendButton = document.getElementById('emailSendButton');
              const originalButtonHTML = sendButton.innerHTML;
              sendButton.disabled = true;
              sendButton.innerHTML = '<svg style="width: 16px; height: 16px; animation: spin 1s linear infinite;" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg> Sending...';
              
              try {
                // Call Firebase function for reports (same function handles both ROI and TAS)
                const response = await fetch('https://us-central1-rooster-tas.cloudfunctions.net/sendROIReportEmail', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    data: {
                      recipientEmail: recipientEmail,
                      recipientName: recipientName || '${formData.contactName || 'Valued Customer'}',
                      ccEmail: ccEmail || undefined,
                      personalMessage: personalMessage || undefined,
                      senderName: '${formData.contactName || ''}',
                      sessionId: '${sessionId || reportId}',  // Use reportId as fallback when viewing from direct link
                      reportId: '${reportId}',
                      reportUrl: '${window.location.origin}/tas-report/${reportId}',  // Always use TAS report URL format
                      formData: {
                        organizationName: '${formData.organizationName || ''}',
                        contactName: '${formData.contactName || ''}',
                        contactEmail: '${formData.contactEmail || ''}',
                        industry: '${formData.industry || ''}'
                      },
                      results: {
                        currentTAS: ${results.currentTAS || 0},
                        projectedTAS: ${results.projectedTAS || 0}
                      }
                    }
                  })
                });
                
                const result = await response.json();
                
                if (result.result && result.result.success) {
                  // Show success message
                  document.getElementById('emailSuccessMessage').style.display = 'flex';
                  
                  // Close dialog after 2 seconds
                  setTimeout(function() {
                    closeEmailDialog();
                    // Reset form
                    document.getElementById('recipientEmail').value = '${formData.contactEmail || ''}';
                    document.getElementById('recipientName').value = '${formData.contactName || ''}';
                    document.getElementById('ccEmail').value = '';
                    document.getElementById('personalMessage').value = '';
                  }, 2000);
                } else {
                  // Show error message
                  document.getElementById('emailErrorText').textContent = (result.error && result.error.message) || 'Failed to send email. Please try again.';
                  document.getElementById('emailErrorMessage').style.display = 'flex';
                }
              } catch (error) {
                // Show error message
                document.getElementById('emailErrorText').textContent = 'An error occurred. Please try again.';
                document.getElementById('emailErrorMessage').style.display = 'flex';
                console.error('Email send error:', error);
              } finally {
                // Re-enable send button
                sendButton.disabled = false;
                sendButton.innerHTML = originalButtonHTML;
              }
            });
          }
          
          // Add spin animation style
          const style = document.createElement('style');
          style.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
          document.head.appendChild(style);
          ` : ''}
        </script>
      </body>
      </html>
    `);

      // Close the document write operation
      targetDocument.close();
    
  } catch (error) {
    console.error('Error generating TAS report:', error);
    alert('There was an error generating the PDF. Please try again.');
  } finally {
    // Remove loading indicator
    document.body.removeChild(loadingDiv);
  }
};

export default generateTasPDFReport;